import { NavLink } from "react-router-dom";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import SchoolIcon from "@mui/icons-material/School";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/auth/useAuth";
import { useBranch } from "@/auth/BranchContext";
import { navGroups } from "@/auth/nav";
import { fetchBranches } from "@/api/branches";
import { useTranslation } from "react-i18next";

export const SIDEBAR_WIDTH = 236;

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const { user, hasPermission } = useAuth();
  const { selectedBranchId, setSelectedBranchId } = useBranch();
  const { t } = useTranslation();
  const sections = user ? navGroups(user.role, user.permissions) : [];
  const canAllBranches = hasPermission("branch.all") && !user?.branchId;
  const { data: branches } = useQuery({
    queryKey: ["branches"],
    queryFn: fetchBranches,
    enabled: canAllBranches,
  });

  const branchLabel = user?.branchId
    ? branches?.find((b) => b.id === user.branchId)?.name ?? "Мой филиал"
    : selectedBranchId === "all"
      ? "Все филиалы"
      : branches?.find((b) => b.id === selectedBranchId)?.name ?? "Филиал";

  return (
    <>
      <Toolbar sx={{ minHeight: "68px !important", px: 2.25 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <SchoolIcon sx={{ color: "white" }} />
          <Typography fontFamily="var(--disp)" fontWeight={700} color="white">
            Pro School
          </Typography>
        </Box>
      </Toolbar>
      <Box mx={2.25} mb={1.5} px={1.25} py={1} borderRadius="9px" bgcolor="var(--rail-2)">
        <Typography color="var(--rail-text-2)" fontSize={10} textTransform="uppercase">Учебный центр</Typography>
        {canAllBranches ? (
          <Select
            size="small"
            value={selectedBranchId}
            onChange={(e) => setSelectedBranchId(e.target.value as "all" | string)}
            sx={{ color: "white", fontSize: 13, fontWeight: 600, ".MuiOutlinedInput-notchedOutline": { border: 0 }, width: "100%", mt: 0.5 }}
          >
            <MenuItem value="all">Все филиалы</MenuItem>
            {(branches ?? []).map((b) => (
              <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
            ))}
          </Select>
        ) : (
          <Typography color="white" fontSize={13} fontWeight={600}>{branchLabel}</Typography>
        )}
      </Box>
      <List sx={{ px: 0, py: 0 }}>
        {sections.map((section) => (
          <Box key={section.group}>
            <Typography
              component="div"
              sx={{
                px: 2.25,
                pt: 1.75,
                pb: 0.75,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: ".08em",
                textTransform: "uppercase",
                color: "var(--rail-text-2)",
              }}
            >
              {section.label}
            </Typography>
            {section.items.map((item) => (
              <ListItemButton
                key={item.path}
                component={NavLink}
                to={item.path}
                end={item.path === "/"}
                onClick={onNavigate}
                sx={{
                  "&.active": {
                    bgcolor: "var(--brand)",
                    color: "white",
                    "& .MuiListItemIcon-root": { color: "primary.contrastText" },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <item.icon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={t(`nav.${item.path === "/" ? "dashboard" : item.path.slice(1)}`, { defaultValue: item.label })}
                />
              </ListItemButton>
            ))}
          </Box>
        ))}
      </List>
      <Box mt="auto" borderTop="1px solid rgb(255 255 255 / 8%)" display="flex" gap={1.25} alignItems="center" p={2}>
        <Avatar sx={{ bgcolor: "var(--brand)", borderRadius: "9px", fontSize: 13, height: 32, width: 32 }}>
          {user?.fullName.slice(0, 1).toUpperCase()}
        </Avatar>
        <Box minWidth={0}>
          <Typography color="white" fontSize={12.5} fontWeight={600} noWrap>{user?.fullName}</Typography>
          <Typography color="var(--rail-text-2)" fontSize={11} noWrap>Личный профиль</Typography>
        </Box>
      </Box>
    </>
  );
}

export function Sidebar({ open, onClose }: SidebarProps) {
  const drawerSx = { [`& .MuiDrawer-paper`]: { boxSizing: "border-box", width: SIDEBAR_WIDTH } };

  return (
    <>
      <Drawer variant="permanent" className="crm-sidebar" sx={{ ...drawerSx, display: { xs: "none", md: "block" }, flexShrink: 0, width: SIDEBAR_WIDTH }}>
        <SidebarContent />
      </Drawer>
      <Drawer variant="temporary" open={open} onClose={onClose} className="crm-sidebar" sx={{ ...drawerSx, display: { md: "none" } }}>
        <SidebarContent onNavigate={onClose} />
      </Drawer>
    </>
  );
}
